import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/supabase';
import { logger } from '../utils/logger';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseServiceRoleKey = import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey || !supabaseServiceRoleKey) {
  logger.error('Missing Supabase environment variables');
  throw new Error('Missing required environment variables: VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY, and VITE_SUPABASE_SERVICE_ROLE_KEY must be set');
}

// Create Supabase client with proper configuration
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  db: {
    schema: 'public'
  }
});

// Create admin client with service role key
export const supabaseAdmin = createClient<Database>(supabaseUrl, supabaseServiceRoleKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: false
  }
});

// Test connection and log result
supabase.from('tasks').select('count', { count: 'exact', head: true })
  .then(() => logger.info('✅ Supabase connection successful'))
  .catch(err => logger.error('❌ Supabase connection failed:', err));

// Utility function to check if Supabase is properly configured
export const isSupabaseConfigured = (): boolean => {
  return Boolean(supabaseUrl && supabaseAnonKey && supabaseServiceRoleKey);
};

// Export configuration for reference
export const supabaseConfig = {
  url: supabaseUrl,
  anonKey: supabaseAnonKey,
  serviceRoleKey: supabaseServiceRoleKey
};

// Initialize Supabase connection
export const initSupabase = async () => {
  try {
    const { error } = await supabase.auth.getSession();
    if (error) {
      throw error;
    }
    logger.info('Supabase initialized successfully');
    return true;
  } catch (error) {
    logger.error('Failed to initialize Supabase:', error);
    return false;
  }
};